import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import OrgPrintChrome from './OrgPrintChrome';
import WhatsAppShareModal from './WhatsAppShareModal';
import useLastRates from '../hooks/useLastRates';
import LastRateHint from './LastRateHint';
import HistoryButton from './HistoryButton';
import { addDaysISO, todayISO } from '../utils/dates';

import DateInput from './DateInput';
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const num = (v) => Number(v || 0);
const VALID_DAYS = 15;

const STATUS = {
  OPEN: ['primary', 'Open'],
  EXPIRED: ['secondary', 'Expired'],
  ACCEPTED: ['success', 'Accepted'],
  DECLINED: ['danger', 'Declined'],
  CANCELLED: ['dark', 'Cancelled']
};
const FILTERS = [['', 'All'], ['OPEN', 'Open'], ['EXPIRED', 'Expired'], ['ACCEPTED', 'Accepted'], ['DECLINED', 'Declined']];

const customerName = (c) => (c?.companyName && c.companyName.trim()) || `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || 'Customer';
const blankLine = () => ({ productId: '', quantity: '1', unitPrice: '', discountPercent: '', taxRateId: '' });

/**
 * Sales > Quotations: write a quote from the product list with GST, send it on WhatsApp, follow it up (accepted,
 * declined, expired), and turn an accepted one into a sale bill or sale order (that form opens filled in).
 */
function Quotations({ active, customers, products, taxRates, orgState, currency = 'INR', onMakeBill, onMakeOrder, reloadKey }) {
  const [quotes, setQuotes] = useState(null);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [sharing, setSharing] = useState(null);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 2 });
    return (v) => f.format(num(v));
  }, [currency]);

  const load = useCallback(() => {
    axios.get('/api/quotations')
      .then((r) => setQuotes(Array.isArray(r.data) ? r.data : []))
      .catch((error) => { setQuotes([]); toast.error(errorMessage(error, 'Could not load quotations')); });
  }, []);

  useEffect(() => { if (active) load(); }, [active, load, reloadKey]);

  const open = async (id) => {
    try {
      setViewing((await axios.get(`/api/quotations/${id}`)).data);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not open the quotation'));
    }
  };

  const startNew = (from) => {
    const today = todayISO();
    setEditing({
      id: null,
      customerId: from ? String(from.customer.id) : '',
      quoteDate: today,
      validUntil: addDaysISO(today, VALID_DAYS),
      notes: from?.notes || '',
      lines: from ? from.lines.map((l) => ({
        productId: String(l.productId), quantity: String(Number(l.quantity)), unitPrice: String(Number(l.unitPrice)),
        discountPercent: l.discountPercent != null ? String(Number(l.discountPercent)) : '', taxRateId: l.taxRateId ? String(l.taxRateId) : ''
      })) : [blankLine()]
    });
  };

  const startEdit = (q) => setEditing({
    id: q.id,
    customerId: String(q.customer.id),
    quoteDate: q.quoteDate,
    validUntil: q.validUntil,
    notes: q.notes || '',
    lines: q.lines.map((l) => ({
      productId: String(l.productId), quantity: String(Number(l.quantity)), unitPrice: String(Number(l.unitPrice)),
      discountPercent: l.discountPercent != null ? String(Number(l.discountPercent)) : '', taxRateId: l.taxRateId ? String(l.taxRateId) : ''
    }))
  });

  const saved = (q) => {
    setEditing(null);
    setViewing(q);
    load();
  };

  const shown = (quotes || []).filter((q) => !filter || q.status === filter);

  if (!active) return null;

  return (
    <div className="mt-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <p className="text-muted mb-0">Quote prices to customers, send them on WhatsApp, and turn accepted ones into a bill or order.</p>
        <Button variant="primary" onClick={() => startNew(null)}>+ New Quotation</Button>
      </div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {FILTERS.map(([key, label]) => (
          <Button key={label} size="sm" variant={filter === key ? 'secondary' : 'outline-secondary'} onClick={() => setFilter(key)}>
            {label}{key && quotes ? ` (${quotes.filter((q) => q.status === key).length})` : ''}
          </Button>
        ))}
      </div>
      {quotes === null ? <div className="py-3 text-center"><Spinner animation="border" size="sm" /></div> : (
        <Table striped bordered hover responsive size="sm">
          <thead>
            <tr><th>Quotation</th><th>Date</th><th>Customer</th><th>Valid until</th><th className="text-end">Total</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-3">
                {quotes.length === 0 ? 'No quotations yet. Click + New Quotation to write one.' : 'None here.'}
              </td></tr>
            )}
            {shown.map((q) => (
              <tr key={q.id}>
                <td className="font-monospace">{q.quoteNumber}</td>
                <td className="text-nowrap">{day(q.quoteDate)}</td>
                <td>{q.customer?.name}</td>
                <td className="text-nowrap">{day(q.validUntil)}</td>
                <td className="text-end text-nowrap">{money(q.total)}</td>
                <td>
                  <Badge bg={(STATUS[q.status] || STATUS.OPEN)[0]}>{(STATUS[q.status] || [null, q.status])[1]}</Badge>
                  {q.convertedNumber && <div className="small text-muted">→ {q.convertedNumber}</div>}
                </td>
                <td className="text-end text-nowrap">
                  <Button size="sm" variant="outline-info" onClick={() => open(q.id)}>View</Button>
                  <HistoryButton documentType="Quotation" documentId={q.id} label={`Quotation ${q.quoteNumber}`} className="ms-1" />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {editing && (
        <QuotationEditor draft={editing} customers={customers} products={products} taxRates={taxRates} orgState={orgState}
          money={money} onHide={() => setEditing(null)} onSaved={saved} />
      )}
      {viewing && (
        <QuotationView quote={viewing} money={money} onHide={() => setViewing(null)}
          onChanged={(q) => { setViewing(q); load(); }}
          onEdit={() => { startEdit(viewing); setViewing(null); }}
          onCopy={() => { startNew(viewing); setViewing(null); }}
          onShare={() => setSharing(viewing)}
          onMakeBill={() => { onMakeBill(viewing); setViewing(null); }}
          onMakeOrder={() => { onMakeOrder(viewing); setViewing(null); }} />
      )}
      {sharing && (
        <WhatsAppShareModal path={`quotations/${sharing.id}`} title={`Send quotation ${sharing.quoteNumber}`}
          what="quotation" offerUpi={false} onHide={() => setSharing(null)} />
      )}
    </div>
  );
}

function QuotationEditor({ draft, customers, products, taxRates, orgState, money, onHide, onSaved }) {
  const [form, setForm] = useState(draft);
  const [saving, setSaving] = useState(false);
  const activeProducts = useMemo(() => (products || []).filter((p) => p.isActive !== false), [products]);
  const rates = useMemo(() => (taxRates || []).filter((t) => t.isActive !== false), [taxRates]);
  const sortedCustomers = useMemo(() => [...(customers || [])].filter((c) => !c.isWalkIn)
    .sort((a, b) => customerName(a).localeCompare(customerName(b))), [customers]);

  const customer = sortedCustomers.find((c) => String(c.id) === String(form.customerId));
  const interState = Boolean(customer?.state && orgState && customer.state.trim().toLowerCase() !== orgState.trim().toLowerCase());

  // What this customer was last charged for each product (or quoted, if never billed)
  const lastRates = useLastRates('customers', form.customerId);
  const lastRatePatch = (last) => ({
    unitPrice: String(Number(last.rate)),
    discountPercent: last.discountPercent ? String(Number(last.discountPercent)) : ''
  });

  const setLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const pickProduct = (i, productId) => {
    const p = activeProducts.find((x) => String(x.id) === String(productId));
    const patch = { productId };
    if (p?.price != null) patch.unitPrice = String(p.price);
    if (lastRates[productId]?.rate != null) Object.assign(patch, lastRatePatch(lastRates[productId]));
    if (p?.defaultGstRate != null) {
      const rate = rates.find((t) => Number(t.ratePercent) === Number(p.defaultGstRate));
      if (rate) patch.taxRateId = String(rate.id);
    }
    setLine(i, patch);
  };

  const lineFigures = (l) => {
    const gross = num(l.quantity) * num(l.unitPrice);
    const off = gross * num(l.discountPercent) / 100;
    const taxable = gross - off;
    const rate = rates.find((t) => String(t.id) === String(l.taxRateId));
    const tax = taxable * num(rate?.ratePercent) / 100;
    return { gross, off, taxable, tax, total: taxable + tax };
  };
  const totals = form.lines.reduce((t, l) => {
    const f = lineFigures(l);
    return { taxable: t.taxable + f.taxable, tax: t.tax + f.tax, total: t.total + f.total, off: t.off + f.off };
  }, { taxable: 0, tax: 0, total: 0, off: 0 });

  const save = async () => {
    if (!form.customerId) { toast.warn('Choose the customer'); return; }
    const lines = form.lines.filter((l) => l.productId);
    if (lines.length === 0) { toast.warn('Add at least one product'); return; }
    const body = {
      customerId: Number(form.customerId),
      quoteDate: form.quoteDate,
      validUntil: form.validUntil,
      notes: form.notes,
      lines: lines.map((l) => ({
        productId: Number(l.productId),
        quantity: num(l.quantity),
        unitPrice: num(l.unitPrice),
        discountPercent: l.discountPercent === '' ? null : num(l.discountPercent),
        taxRateId: l.taxRateId ? Number(l.taxRateId) : null
      }))
    };
    setSaving(true);
    try {
      const { data } = form.id ? await axios.put(`/api/quotations/${form.id}`, body) : await axios.post('/api/quotations', body);
      toast.success(form.id ? 'Quotation saved' : `Quotation ${data.quoteNumber} created`);
      onSaved(data);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the quotation'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title as="h5">{form.id ? 'Edit quotation' : 'New quotation'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3 mb-3">
          <Col md={6}>
            <Form.Label>Customer</Form.Label>
            <Form.Select value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })}>
              <option value="">Choose the customer</option>
              {sortedCustomers.map((c) => <option key={c.id} value={c.id}>{customerName(c)}{c.city ? ` · ${c.city}` : ''}</option>)}
            </Form.Select>
            <Form.Text className="text-muted">New customer? Add them under Customers first.</Form.Text>
          </Col>
          <Col md={3}>
            <Form.Label>Date</Form.Label>
            <DateInput value={form.quoteDate}
              onChange={(e) => setForm({ ...form, quoteDate: e.target.value, validUntil: e.target.value ? addDaysISO(e.target.value, VALID_DAYS) : form.validUntil })} />
          </Col>
          <Col md={3}>
            <Form.Label>Valid until</Form.Label>
            <DateInput value={form.validUntil} min={form.quoteDate} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
          </Col>
        </Row>
        <Table bordered size="sm" responsive>
          <thead>
            <tr>
              <th style={{ minWidth: '14rem' }}>Product</th><th style={{ width: '7rem' }}>Qty</th><th style={{ width: '8rem' }}>Rate</th>
              <th style={{ width: '6rem' }}>Disc. %</th><th style={{ width: '9rem' }}>GST</th><th className="text-end" style={{ width: '9rem' }}>Amount</th><th style={{ width: '2.5rem' }} />
            </tr>
          </thead>
          <tbody>
            {form.lines.map((l, i) => {
              const f = lineFigures(l);
              const unit = activeProducts.find((p) => String(p.id) === String(l.productId))?.unitOfMeasure;
              return (
                <tr key={i}>
                  <td>
                    <Form.Select size="sm" value={l.productId} onChange={(e) => pickProduct(i, e.target.value)}>
                      <option value="">Choose a product</option>
                      {activeProducts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Form.Select>
                  </td>
                  <td>
                    <div className="d-flex align-items-center gap-1">
                      <Form.Control size="sm" type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} />
                      {unit && <span className="small text-muted">{unit}</span>}
                    </div>
                  </td>
                  <td>
                    <Form.Control size="sm" type="number" min="0" step="0.01" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} />
                    <LastRateHint last={lastRates[l.productId]} current={l.unitPrice} onUse={() => setLine(i, lastRatePatch(lastRates[l.productId]))} />
                  </td>
                  <td><Form.Control size="sm" type="number" min="0" max="100" step="0.01" value={l.discountPercent} onChange={(e) => setLine(i, { discountPercent: e.target.value })} /></td>
                  <td>
                    <Form.Select size="sm" value={l.taxRateId} onChange={(e) => setLine(i, { taxRateId: e.target.value })}>
                      <option value="">No GST</option>
                      {rates.map((t) => <option key={t.id} value={t.id}>{t.name || `GST ${Number(t.ratePercent)}%`}</option>)}
                    </Form.Select>
                  </td>
                  <td className="text-end align-middle text-nowrap">{money(f.total)}</td>
                  <td className="align-middle">
                    {form.lines.length > 1 && (
                      <Button size="sm" variant="outline-danger" className="py-0" onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}>×</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Button size="sm" variant="outline-primary" onClick={() => setForm({ ...form, lines: [...form.lines, blankLine()] })}>+ Add line</Button>
        <Row className="g-3 mt-1">
          <Col md={7}>
            <Form.Label>Terms and notes</Form.Label>
            <Form.Control as="textarea" rows={4} maxLength={2000} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Delivery time, payment terms, freight, packing..." />
          </Col>
          <Col md={5}>
            <Table size="sm" className="mb-0">
              <tbody>
                {totals.off > 0 && <tr><td>Discount</td><td className="text-end">-{money(totals.off)}</td></tr>}
                <tr><td>Taxable value</td><td className="text-end">{money(totals.taxable)}</td></tr>
                <tr><td>{interState ? 'IGST' : 'CGST + SGST'}</td><td className="text-end">{money(totals.tax)}</td></tr>
                <tr className="fw-bold"><td>Total</td><td className="text-end">{money(totals.total)}</td></tr>
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save quotation'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function QuotationView({ quote: q, money, onHide, onChanged, onEdit, onCopy, onShare, onMakeBill, onMakeOrder }) {
  const [busy, setBusy] = useState(false);
  const isOpen = q.status === 'OPEN' || q.status === 'EXPIRED';
  const converted = Boolean(q.convertedId);
  const canConvert = !converted && q.status !== 'DECLINED' && q.status !== 'CANCELLED';

  const setStatus = async (status, ask) => {
    let reason = null;
    if (ask) {
      reason = window.prompt(ask);
      if (reason === null) return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`/api/quotations/${q.id}/status`, { status, reason });
      toast.success(status === 'OPEN' ? 'Quotation open again' : `Marked ${status.toLowerCase()}`);
      onChanged(data);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not change it'));
    } finally {
      setBusy(false);
    }
  };

  const [variant, label] = STATUS[q.status] || STATUS.OPEN;

  return (
    <Modal show onHide={onHide} size="lg" dialogClassName="print-modal">
      <Modal.Header closeButton className="print-header">
        <Modal.Title as="h5">Quotation {q.quoteNumber} <Badge bg={variant} className="ms-2 align-middle">{label}</Badge></Modal.Title>
      </Modal.Header>
      <Modal.Body className="print-body">
        <div className="print-actions d-flex flex-wrap gap-2 mb-3">
          {q.status !== 'CANCELLED' && <Button size="sm" variant="success" onClick={onShare}><i className="bi bi-whatsapp me-1" />Send on WhatsApp</Button>}
          <Button size="sm" variant="outline-secondary" onClick={() => window.print()}><i className="bi bi-printer me-1" />Print / PDF</Button>
          {isOpen && !converted && <Button size="sm" variant="outline-primary" onClick={onEdit}>Edit</Button>}
          <Button size="sm" variant="outline-primary" onClick={onCopy}>Copy as new</Button>
        </div>
        {canConvert && (
          <Alert variant={q.status === 'ACCEPTED' ? 'success' : 'light'} className="print-actions small py-2 d-flex flex-wrap align-items-center gap-2">
            <span className="me-auto">{q.status === 'ACCEPTED' ? 'Accepted. Make the bill or order from it:' : 'Customer said yes? Make the bill or order from it:'}</span>
            <Button size="sm" onClick={onMakeBill}>Make sale bill</Button>
            <Button size="sm" variant="outline-primary" onClick={onMakeOrder}>Make sale order</Button>
          </Alert>
        )}
        {converted && (
          <Alert variant="success" className="print-actions small py-2">
            Became {q.convertedKind === 'ORDER' ? 'sale order' : 'sale bill'} <strong>{q.convertedNumber}</strong>.
          </Alert>
        )}
        {q.statusReason && <Alert variant="light" className="print-actions small py-2">Reason: {q.statusReason}</Alert>}

        <OrgPrintChrome>
          <div className="print-content">
            <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
              <div>
                <div className="small text-muted text-uppercase">Quotation</div>
                <div className="fs-5 fw-bold">{q.quoteNumber}</div>
                <div className="small">Date {day(q.quoteDate)} · Valid until {day(q.validUntil)}</div>
              </div>
              <div className="text-md-end">
                <div className="small text-muted">To</div>
                <div className="fw-semibold">{q.customer?.name}</div>
                {q.customer?.phone && <div className="small">{q.customer.phone}</div>}
              </div>
            </div>
            <Table bordered size="sm" className="small">
              <thead>
                <tr>
                  <th>#</th><th>Item</th><th>HSN</th><th className="text-end">Qty</th><th className="text-end">Rate</th>
                  <th className="text-end">Disc.</th><th className="text-end">Taxable</th><th className="text-end">GST</th><th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {q.lines.map((l, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{l.description}</td>
                    <td>{l.hsn || ''}</td>
                    <td className="text-end text-nowrap">{Number(l.quantity).toLocaleString('en-IN', { maximumFractionDigits: 3 })} {l.unit || ''}</td>
                    <td className="text-end">{money(l.unitPrice)}</td>
                    <td className="text-end">{l.discountPercent != null ? `${Number(l.discountPercent)}%` : ''}</td>
                    <td className="text-end">{money(l.taxable)}</td>
                    <td className="text-end text-nowrap">{l.gstRate != null ? `${Number(l.gstRate)}% ` : ''}{money(l.tax)}</td>
                    <td className="text-end">{money(l.total)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex flex-wrap justify-content-between gap-3">
              <div style={{ whiteSpace: 'pre-line', maxWidth: '26rem' }} className="small">
                {q.notes && <><div className="text-muted">Terms and notes</div>{q.notes}</>}
              </div>
              <Table size="sm" className="small mb-0" style={{ maxWidth: '20rem' }}>
                <tbody>
                  {num(q.discount) > 0 && <tr><td>Discount</td><td className="text-end">-{money(q.discount)}</td></tr>}
                  <tr><td>Taxable value</td><td className="text-end">{money(q.taxable)}</td></tr>
                  {num(q.cgst) > 0 && <tr><td>CGST</td><td className="text-end">{money(q.cgst)}</td></tr>}
                  {num(q.sgst) > 0 && <tr><td>SGST</td><td className="text-end">{money(q.sgst)}</td></tr>}
                  {num(q.igst) > 0 && <tr><td>IGST</td><td className="text-end">{money(q.igst)}</td></tr>}
                  <tr className="fw-bold"><td>Total</td><td className="text-end">{money(q.total)}</td></tr>
                </tbody>
              </Table>
            </div>
          </div>
        </OrgPrintChrome>
      </Modal.Body>
      <Modal.Footer className="print-actions">
        {isOpen && !converted && (
          <>
            <Button variant="outline-success" size="sm" disabled={busy} onClick={() => setStatus('ACCEPTED')}>Mark accepted</Button>
            <Button variant="outline-danger" size="sm" disabled={busy} onClick={() => setStatus('DECLINED', 'Why did they decline? (optional)')}>Mark declined</Button>
            <Button variant="outline-secondary" size="sm" disabled={busy} onClick={() => setStatus('CANCELLED', 'Why cancel it? (optional)')}>Cancel quotation</Button>
          </>
        )}
        {!converted && (q.status === 'DECLINED' || q.status === 'CANCELLED' || q.status === 'ACCEPTED') && (
          <Button variant="outline-secondary" size="sm" disabled={busy} onClick={() => setStatus('OPEN')}>Open again</Button>
        )}
        <Button variant="secondary" size="sm" className="ms-auto" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default Quotations;
